import { isValidPhoneNumber as isValidPhoneNumberLib } from 'libphonenumber-js';
import { types as sdkTypes } from './sdkLoader';
import { diffInTime } from './dates';
import { extractYouTubeID } from './string';

const { LatLng, Money } = sdkTypes;

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 256;

const isNonEmptyString = val => {
  return typeof val === 'string' && val.trim().length > 0;
};

/**
 * Validator functions and helpers for Final Forms
 */

// Final Form expects and undefined value for a successful validation
const VALID = undefined;

export const required = message => value => {
  if (typeof value === 'undefined' || value === null) {
    // undefined or null values are invalid
    return message;
  }
  if (typeof value === 'string') {
    // string must be nonempty when trimmed
    return isNonEmptyString(value) ? VALID : message;
  }
  return VALID;
};

export const requiredStringNoTrim = message => value => {
  return typeof value === 'string' && value.length > 0 ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredBoolean = message => value => {
  return typeof value === 'boolean' ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredAndNonEmptyString = message => value => {
  return isNonEmptyString(value) ? VALID : message;
};

/**
 * Validates that a string is unique in an array of strings.
 * @param {Array<string>} stringArray - Array of strings to check against.
 * @param {Function} getMessage - Function that returns an error message.
 * @param {Function} toSlug - Function that converts a string to a slug.
 * @returns {string} - VALID if the string is unique, otherwise the error message.
 */
export const uniqueString = (currentIndex, stringArray, getMessage, toSlug) => value => {
  if (typeof value === 'undefined' || value === null) {
    // undefined or null values are invalid
    return getMessage('', '');
  }
  const slug = toSlug(value);
  const otherSlugs = stringArray.map(toSlug).filter((_, i) => i !== currentIndex);
  const isUnique = !otherSlugs.includes(slug);
  return isUnique ? VALID : getMessage(value, slug);
};

export const requiredFieldArrayCheckbox = message => value => {
  if (!value) {
    return message;
  }

  const entries = Object.entries(value);
  const hasSelectedValues = entries.filter(e => !!e[1]).length > 0;
  return hasSelectedValues ? VALID : message;
};

export const requiredSelectTreeOption = message => value => {
  if (typeof value === 'undefined' || value === null || Object.values(value)?.length === 0) {
    return message;
  }
};

export const minLength = (message, minimumLength) => value => {
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length >= minimumLength ? VALID : message;
};

export const maxLength = (message, maximumLength) => value => {
  if (!value) {
    return VALID;
  }
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length <= maximumLength ? VALID : message;
};

export const nonEmptyArray = message => value => {
  return value && Array.isArray(value) && value.length > 0 ? VALID : message;
};

export const autocompleteSearchRequired = message => value => {
  return value && value.search ? VALID : message;
};

export const autocompletePlaceSelected = message => value => {
  const selectedPlaceIsValid =
    value &&
    value.selectedPlace &&
    value.selectedPlace.address &&
    value.selectedPlace.origin instanceof LatLng;
  return selectedPlaceIsValid ? VALID : message;
};

export const bookingDateRequired = inValidDateMessage => value => {
  const dateObj = value?.date || (value instanceof Date ? value : null);
  const dateIsValid = dateObj instanceof Date && !isNaN(dateObj.getTime());
  return !dateIsValid ? inValidDateMessage : VALID;
};

export const bookingDatesRequired = (inValidStartDateMessage, inValidEndDateMessage) => value => {
  const startDateIsValid = value && value.startDate instanceof Date;
  const endDateIsValid = value && value.endDate instanceof Date;

  if (!startDateIsValid) {
    return inValidStartDateMessage;
  } else if (!endDateIsValid) {
    return inValidEndDateMessage;
  } else {
    return VALID;
  }
};

// Source: http://www.regular-expressions.info/email.html
// See the link above for an explanation of the tradeoffs.
const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const emailFormatValid = message => value => {
  return value && EMAIL_RE.test(value) ? VALID : message;
};

export const moneySubUnitAmountAtLeast = (message, minValue) => value => {
  return value instanceof Money && value.amount >= minValue ? VALID : message;
};

const parseNum = str => {
  const num = Number.parseInt(str, 10);
  return Number.isNaN(num) ? null : num;
};

export const numberAtLeast = (message, minNumber) => value => {
  const valueNum = parseNum(value);
  return typeof valueNum === 'number' && valueNum >= minNumber ? VALID : message;
};

export const validateInteger = (value, max, min, numberTooSmallMessage, numberTooBigMessage) => {
  const parsedValue = Number.parseInt(value, 10);
  if (parsedValue > max) {
    return numberTooBigMessage;
  }
  if (parsedValue < min) {
    return numberTooSmallMessage;
  }
  return VALID;
};

// If URL is passed to this function as null, will return VALID
export const validateYoutubeURL = (url, message) => {
  return url ? (extractYouTubeID(url) ? VALID : message) : VALID;
};

export const ageAtLeast = (message, minYears) => value => {
  const { year, month, day } = value;
  const dayNum = parseNum(day);
  const monthNum = parseNum(month);
  const yearNum = parseNum(year);

  // day, month, and year needs to be numbers
  if (dayNum !== null && monthNum !== null && yearNum !== null) {
    const now = new Date();
    const age = new Date(yearNum, monthNum - 1, dayNum);
    const ageInYears = diffInTime(now, age, 'years', true);

    return age && age instanceof Date && ageInYears >= minYears ? VALID : message;
  }
  return message;
};

export const validBusinessURL = message => value => {
  if (typeof value === 'undefined' || value === null) {
    return message;
  }

  const disallowedChars = /[^-A-Za-z0-9+&@#/%?=~_|!:,.;()]/;
  const protocolTokens = value.split(':');
  const includesProtocol = protocolTokens.length > 1;
  const usesHttpProtocol = includesProtocol && !!protocolTokens[0].match(/^(https?)/);

  const invalidCharacters = !!value.match(disallowedChars);
  const invalidProtocol = !(usesHttpProtocol || !includesProtocol);
  // Stripe checks against example.com
  const isExampleDotCom = !!value.match(/^(https?:\/\/example\.com|example\.com)/);
  const isLocalhost = !!value.match(/^(https?:\/\/localhost($|:|\/)|localhost($|:|\/))/);
  return invalidCharacters || invalidProtocol || isExampleDotCom || isLocalhost ? message : VALID;
};

export const validSsnLast4 = message => value => {
  return value.length === 4 ? VALID : message;
};

export const validHKID = message => value => {
  // Accept value 000000000 for testing Stripe
  if (value.length === 9 && value.match(/([0]{9})/)) {
    return VALID;
  }

  // HKID format example: AB364912(5)
  // ID can start with one or two letters and the check digit in the end can be in brackets or not
  if (value.length < 8) {
    return message;
  }

  // Handle possible brackets in value
  if (value.charAt(value.length - 3) === '(' && value.charAt(value.length - 1) === ')') {
    value = value.substring(0, value.length - 3) + value.charAt(value.length - 2);
  }
  value = value.toUpperCase();

  // Check that pattern is correct and split value to array
  const hkidPattern = /^([A-Z]{1,2})([0-9]{6})([A0-9])$/;
  const matchArray = value.match(hkidPattern);

  if (!matchArray) {
    return message;
  }

  const charPart = matchArray[1];
  const numPart = matchArray[2];
  const checkDigit = matchArray[3];

  // Calculate the checksum for character part.
  // Transfer letters to numbers so that A=10, B=11, C=12 etc.
  // If there is only one letter in the ID use 36 as the first value
  // Total calculation is weighted so that 1st digit is x9, 2nd digit x8, 3rd digit x7 etc.

  const strValidChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let checkSum = 0;

  if (charPart.length === 2) {
    checkSum += 9 * (10 + strValidChars.indexOf(charPart.charAt(0)));
    checkSum += 8 * (10 + strValidChars.indexOf(charPart.charAt(1)));
  } else {
    checkSum += 9 * 36;
    checkSum += 8 * (10 + strValidChars.indexOf(charPart));
  }

  // Calculate the checksum for numeric part

  for (let i = 0, j = 7; i < numPart.length; i++, j--) {
    checkSum += j * numPart.charAt(i);
  }

  // Verify the check digit
  const remaining = checkSum % 11;
  let verify = remaining === 0 ? 0 : 11 - remaining;
  verify = verify.toString();
  const isValid = verify === checkDigit || (verify === 10 && checkDigit === 'A');

  return isValid ? VALID : message;
};

export const validSGID = message => value => {
  return value.length === 9 ? VALID : message;
};

export const composeValidators = (...validators) => (value, allValues) =>
  validators.reduce((error, validator) => error || validator(value, allValues), VALID);

export const validateLengthByCountry = (dialCode, rawNationalDigits) => {
  const code = (dialCode || '').replace(/\D/g, '');
  const len = (rawNationalDigits || '').replace(/\D/g, '').length;

  if (code === '91') {
    return len === 10;
  }
  if (code === '1') {
    return len === 10;
  }
  if (code === '33') {
    return len === 9;
  }
  if (code === '44' || code === '49') {
    return len >= 10 && len <= 11;
  }
  if (code === '81') {
    return len === 10;
  }
  if (code === '86') {
    return len === 11;
  }
  if (code === '61') {
    return len === 9;
  }

  return len >= 7 && len <= 15;
};

export const isInvalidPhoneNumber = (rawDigits, countryCode = '') => {
  if (!rawDigits || typeof rawDigits !== 'string') {
    return false;
  }
  const str = rawDigits.trim();
  const digits = str.replace(/\D/g, '');
  if (!digits) {
    return false;
  }

  // All digits in the whole string are identical (e.g. 0000000000, 1111111111)
  if (/^(\d)\1+$/.test(digits)) {
    return true;
  }

  const cleanCountryCode = (countryCode || '').trim();
  const codeDigits = cleanCountryCode.replace(/\D/g, '');

  let phoneStringToTest = '';

  if (str.startsWith('+')) {
    phoneStringToTest = str;
  } else if (codeDigits && digits.startsWith(codeDigits)) {
    phoneStringToTest = `+${digits}`;
  } else if (codeDigits && !/^[A-Za-z]{2}$/.test(cleanCountryCode)) {
    phoneStringToTest = `+${codeDigits}${digits.replace(/^0+/, '')}`;
  } else if (digits.length >= 7) {
    phoneStringToTest = `+${digits}`;
  }

  let isLibValid = false;

  if (phoneStringToTest) {
    try {
      if (isValidPhoneNumberLib(phoneStringToTest)) {
        isLibValid = true;
      }
    } catch (e) {
      // ignore
    }
  }

  if (!isLibValid && cleanCountryCode && /^[A-Za-z]{2}$/.test(cleanCountryCode)) {
    try {
      if (isValidPhoneNumberLib(str, cleanCountryCode.toUpperCase())) {
        isLibValid = true;
      }
    } catch (e) {
      // ignore
    }
  }

  if (!isLibValid) {
    return true;
  }

  const nationalDigitsList = [];
  if (codeDigits && digits.startsWith(codeDigits)) {
    nationalDigitsList.push(digits.slice(codeDigits.length));
  } else {
    nationalDigitsList.push(digits);
    if (digits.length > 7) {
      nationalDigitsList.push(digits.slice(1));
      nationalDigitsList.push(digits.slice(2));
      nationalDigitsList.push(digits.slice(3));
    }
  }

  for (const nationalDigits of nationalDigitsList) {
    if (nationalDigits && nationalDigits.length >= 6) {
      // Reject national subscriber digits that are all zeros (e.g. 0000000000)
      if (/^0+$/.test(nationalDigits)) {
        return true;
      }
      // Reject single repeating digit in national subscriber number (e.g. 1111111111)
      if (/^(\d)\1+$/.test(nationalDigits)) {
        return true;
      }
      // Reject sequential fake numbers (e.g. 1234567890, 0123456789, 9876543210)
      if (
        nationalDigits === '1234567890' ||
        nationalDigits === '0123456789' ||
        nationalDigits === '9876543210'
      ) {
        return true;
      }
    }
  }

  return false;
};

export const validPhoneNumber = (message, countryCodeName) => (value, allValues) => {
  if (!value || typeof value !== 'string' || value.trim() === '') {
    return VALID;
  }
  const trimmedValue = value.trim();
  const rawDigits = trimmedValue.replace(/\D/g, '');

  let countryCode = '';
  if (countryCodeName && allValues && allValues[countryCodeName]) {
    countryCode = allValues[countryCodeName];
  }

  if (isInvalidPhoneNumber(trimmedValue, countryCode)) {
    return message;
  }

  const isValidLength = validateLengthByCountry(countryCode, rawDigits);
  if (!isValidLength) {
    return message;
  }

  return VALID;
};

export const passwordComplexityValid = message => value => {
  if (!value || typeof value !== 'string') {
    return VALID;
  }
  const hasCapital = /[A-Z]/.test(value);
  const hasNumber = /[0-9]/.test(value);
  const hasSpecial = /[^A-Za-z0-9]/.test(value);

  return hasCapital && hasNumber && hasSpecial ? VALID : message;
};

export const isAlphaNumeric = message => value => {
  return !value || /^[a-zA-Z0-9\s]+$/.test(value) ? VALID : message;
};

